import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query/react';
import { t as tt } from 'i18next';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';
import Content from '../components/mplat/Content';
import Header1 from '../components/mplat/Header1';
import MultiSelect from '../components/mplat/MultiSelect';
import Permissioner from '../components/mplat/Permissioner';
import Section from '../components/mplat/Section';
import Select from '../components/mplat/Select';
import TextArea from '../components/mplat/TextArea';
import TextInput from '../components/mplat/TextInput';
import { useAcc, useAuth, useInput, useLanguage, useOTP, useRole } from '../hooks';
import {
    ACCESS_TYPES,
    ContentRequest,
    ContentUnitFull,
    TYPS,
    useCreateContentUnitMutation,
    useCreateContentUserBooksItemMutation,
    useCreatePublicContentMutation,
    useCreateUserBooksItemMutation,
    useDeleteContentUnitMutation,
    useDeleteContentUserBooksItemMutation,
    useDeleteUserBooksItemMutation,
    useEditContentUnitMutation,
    useGetContentUserBookQuery,
    useGetReferenceBookQuery,
    useGetUserBookQuery,
    useLazyGetMediaUnitQuery,
    useMakeCensoredUnitMutation, useUnmakeCensoredUnitMutation,
    useUpdateContentCmsMutation
} from '../services/content_v1';
// import { ReactComponent as ArrowLeft } from '../assets/svg/ArrowLeftSmall.svg';
import { S3Client } from '@aws-sdk/client-s3';
import { CreateButton } from '../apps/ContentEditor/components/CreateButton';
import { FixedButtonGroup } from '../apps/ContentEditor/components/FixedButtonGroup';
import { TranscodeModal } from '../apps/ContentEditor/components/TranscodeModal';
import { createS3ClientWithMD5 } from '../apps/Storage/components/md5ClientS3';
import { StorageFolderSelection } from '../apps/Storage/components/StorageMy';
import { uploadFile } from '../apps/Storage/services';
import { ReactComponent as Copy } from '../assets/svg/Copy.svg';
import { ReactComponent as Edit } from '../assets/svg/Edit.svg';
import { ReactComponent as FileShadow } from '../assets/svg/File.svg';
import { useAlert } from '../components/alert';
import { extractErrorDetail, extractS3Error, isConsumer, isProvider, ROLE, toMplatBackendDate, useIsAdmin } from '../components/common';
import Button from '../components/mplat/Button/Button';
import ContentAddBody from '../components/mplat/ContentAddBody';
import ContentLeftSideSticky, { ContentLeftSideItem } from '../components/mplat/ContentLeftSideSticky';
import Form from '../components/mplat/Form';
import FormEntryFull from '../components/mplat/FormEntryFull';
import FormUploaderZone from '../components/mplat/FormUploaderZone';
import { InputSwitch } from '../components/mplat/InputSwitch';
import Label from '../components/mplat/Label';
import { Modal } from '../components/mplat/Modal';
import { ErrorModal } from '../components/mplat/Modals/ErrorModal';
import { SendModal } from '../components/mplat/Modals/SendModal';
import NumberInput from '../components/mplat/NumberInput';
import { SelectButton } from '../components/mplat/SelectButton';
import UnitCoverUploaderV1 from '../components/mplat/UnitCoverUploaderV1';
import UnitTrailerUploaderV1 from '../components/mplat/UnitTrailerUploaderV1';
import { AVAIL_VIDEO_FORMATS } from '../const';
import { useCallbackPrompt } from '../hooks/useCallbackPrompt';
import { useMounted } from '../hooks/useMounted';
import { useNavigateSearch } from '../hooks/useNavigateSearch';
import { StatusResponse } from '../services/api';
import { useGetTemporalAccessKeyMutation, useLazyGetSdnConfigQuery, useLazyGetSdnDisksQuery } from '../services/sdn';
import { useGetTranscodeProfilesQuery } from '../services/transcode';
import { AccountResType, useGetAccountListQuery, useGetCurrentAccountTariffQuery } from '../services/users_v1';
import MyHttpHandler from '../utils/MyHttpHandler';
import NotFoundPage from './NotFoundPage';

interface S3TrailerUploaderProps {
    client: S3Client,
    myHttpHandler: MyHttpHandler,
    bucket: string,
    prefix: string,
    moderation?: boolean,
    onSuccess: () => void,
}

export function S3TrailerUploader({ client, myHttpHandler, bucket, prefix, moderation, onSuccess }: S3TrailerUploaderProps) {
    const { t } = useTranslation();
    const alert = useAlert();
    const refUpload = useRef<HTMLInputElement | null>(null);
    const [progress, setProgress] = useState<number | null>(null);
    const [filename, setFilename] = useState('');
    const abortController = useMemo(() => new AbortController(), []);

    function validateTrailerFile(f: File) {
        if (f.size > 1024 * 1024 * 1024) {
            // more 1024Mb
            return false;
        }
        const extention = f.name.split('.').pop() || '';
        if (!['mp4', 'webm', 'ogg'].includes(extention.toLocaleLowerCase()) && !sessionStorage.skipFileCheck) {
            return false;
        }
        return true;
    }

    const handleFiles = async (handledFiles: FileList | null) => {
        if (!handledFiles) return;
        if (!validateTrailerFile(handledFiles[0])) {
            alert.error(t('modal:UPLOAD_TRAILER_ERROR_FORMAT'));
            return;
        }
        const f = handledFiles[0];
        setFilename(f.name);
        setProgress(0);

        client && uploadFile(client, bucket, prefix, f, f.name, myHttpHandler, (path, p) => {
            setProgress(p);
        }, abortController.signal)
            .then(() => {
                setProgress(100);
                setTimeout(() => setFilename(''), 2000);
                onSuccess();
            })
            .catch((e) => {
                if (e.name === 'AbortError') {
                    console.log(e);
                } else {
                    alert.error(t(extractS3Error(e)));
                }
            });
    };

    return (
        <div className="form__uploader _video" style={{ paddingRight: moderation ? undefined : '0px' }}>
            {filename && <div className="form__uploader-progress" style={{ display: 'block' }}>
                <div className={'form__uploader-progress-item'} style={{ display: 'block' }}>
                    <div className="form__uploader-progress-item">
                        <div className="form__uploader-progress-item-icon">
                            <FileShadow />
                        </div>
                        <div className="form__uploader-progress-item-bar">
                            <div className="form__uploader-progress-item-bar-name">{filename}</div>
                            <div className="form__uploader-progress-item-bar-line">
                                <div className="form__uploader-progress-item-bar-line-bar">
                                    <div className="form__uploader-progress-item-bar-line-bar-inner" style={{ width: `${progress}%` }}>
                                    </div>
                                </div>
                                <div className="form__uploader-progress-item-bar-line-percentage">{progress?.toFixed(1)}%</div>
                            </div>
                        </div>
                        {(progress !== null && progress < 100) && <div className="form__uploader-progress-item-cancel" onClick={async () => abortController.abort()}>
                            <Trans>Cancel</Trans>
                        </div>}
                    </div>
                </div>
            </div>}
            <FormUploaderZone
                style={{ opacity: filename ? 0.5 : 1 }}
                hint={<>
                    <Trans>
                        Drag file here
                    </Trans>
                    <br />
                    <span>
                        <Trans>
                            or select on computer
                        </Trans>
                    </span>
                </>}
            >
                <input
                    ref={refUpload}
                    type="file"
                    disabled={!!filename}
                    className="form__uploader-input"
                    data-test-id="trailerUploaderInput"
                    accept=".mp4,.MP4,.webm,.WEBM,.ogg,.OGG"
                    onChange={async (e) => {
                        if (e.target.files) {
                            handleFiles(e.target.files);
                        }
                        e.preventDefault();
                        // chrome fix
                        e.target.value = '';
                        e.target.files = null;
                    }}
                />
                <Trans>Load demo</Trans>
                <br />
                <Trans>
                    Supported file formats mp4, webm и ogg
                </Trans>
                <br />
                <Trans>limit</Trans> 1 Gb
            </FormUploaderZone>
        </div>
    )
}


// interface SendButtonProps {
//     onClick?: React.MouseEventHandler<HTMLButtonElement>
// }

// function SendButton({ onClick }: SendButtonProps) {
//     const { ref, inView } = useInView({
//         threshold: 1
//     });

//     return (
//         <div ref={ref} className={'content-add__submit' + (inView ? '' : ' _fixed')}>
//             <div className="content-add__submit-inner">
//                 <button data-test-id="contentSendButton" type="submit" className="btn _bd-accent" onClick={onClick}>
//                     <span className="btn__icon _right">
//                         <Trans>Send</Trans>
//                         <ArrowLeft />
//                     </span>
//                 </button>
//             </div>
//         </div>
//     );
// }

export function extractAccount(fullPrefix: string) {
    const regex = /(account\d+)\/.*/;
    const res = regex.exec(fullPrefix);
    return res ? res[1] || '' : '';
}

export function extractPrefix(fullPrefix: string) {
    const regex = /account\d+\/(.*)/;
    const res = regex.exec(fullPrefix);
    return res ? res[1] || '' : '';
}

export function getCardPrefix(account: string, prefix: string) {
    const path = `account${account}/${prefix.startsWith('/') ? prefix.slice(1) : prefix}`;
    return path.endsWith('/') ? path : (path + '/');
}

function ContentEditorPage() {
    const ALL_CATEGORY = useMemo(() => tt('content:ALL'), [tt]);
    // hooks
    const auth = useAuth();
    const alert = useAlert();
    const myAcc = useAcc();
    const otp = useOTP();
    const myLocation = useLocation();
    const role = useRole();
    const { id } = useParams();
    const { t } = useTranslation();
    const lang = useLanguage();
    const isAdmin = useIsAdmin();
    const isMounted = useMounted();
    const navigate = useNavigateSearch(window.location.pathname);
    // fetches
    const { data: tariff } = useGetCurrentAccountTariffQuery();
    const [fetchUnit, { data: rawData, error: contentError }] = useLazyGetMediaUnitQuery();
    // const [fetchMedia, { data: onlyMedia }] = useLazyGetContentUnitMediaQuery();
    const { data: refBooks } = useGetReferenceBookQuery();
    const { data: transProfiles } = useGetTranscodeProfilesQuery();
    const { data: refUserBooks, refetch: refetchUserBooks } = parseInt(id || '') ? useGetContentUserBookQuery(parseInt(id || '')) : useGetUserBookQuery();
    const { data: consumers, refetch: fetchConsumers } = useGetAccountListQuery({ offset: 0, limit: -1, rtype: AccountResType.CONSUMER });
    const { data: providers, refetch: fetchProviders } = useGetAccountListQuery({ offset: 0, limit: -1, rtype: AccountResType.PROVIDER });
    // const { data: quotas } = useGetQuotasQuery();

    const [createItem] = useCreateUserBooksItemMutation();
    const [createContentItem] = useCreateContentUserBooksItemMutation();

    const [deleteItem] = useDeleteUserBooksItemMutation();
    const [deleteContentItem] = useDeleteContentUserBooksItemMutation();

    // const [attachFile] = useAttachFileMutation();

    const [createRequest] = useCreateContentUnitMutation();
    // const [createDraftRequest] = useCreateDraftUnitMutation();
    const [makeCensoredRequest] = useMakeCensoredUnitMutation();
    const [unmakeCensoredRequest] = useUnmakeCensoredUnitMutation();
    const [editRequest] = useEditContentUnitMutation();
    const [deleteRequest] = useDeleteContentUnitMutation();
    const [updateCmsRequest] = useUpdateContentCmsMutation();

    const [fetchSdnConfig] = useLazyGetSdnConfigQuery();
    const [fetchSdnDisks] = useLazyGetSdnDisksQuery();
    const [getTempKey] = useGetTemporalAccessKeyMutation();

    const [fetchCreatePublicLink] = useCreatePublicContentMutation();
    // states
    const [createForm, setCreateForm] = useState(id === 'create');
    const [data, setData] = useState<StatusResponse<ContentUnitFull>>();

    // const { data: subacc } = useGetAccountSubAccountsQuery({tag: undefined});
    const [unitId, setUnitId] = useState(parseInt(id || ''));
    const [isEdit, setIsEdit] = useState(!isNaN(unitId));
    const [emptyTitle, setEmptyTitle] = useState<boolean | undefined>();

    const [clientMap, setClientMap] = useState<{ [id: number]: { id?: number, username?: string, company?: string } }>({});

    const [error, setError] = useState<string>('');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [errorSave, setErrorSave] = useState<any>(null);
    const [errorTitle, setErrorTitle] = useState<string>('');
    const [title, resetTitle] = useInput('');
    const [titleEng, resetTitleEng] = useInput('');
    const [owner, setOwner] = useState(Number(auth.account));
    const [hasCover, setHasCover] = useState(false);
    const [hasTrailer, setHasTrailer] = useState(false);
    const [unitType, resetUnitType] = useInput('');
    const [qualities, setQualities] = useState<string[]>([]);
    const [collections, setCollections] = useState<number[]>([]);
    const [startyear, setStartyear] = useState<number | undefined>();
    const [genres, setGenres] = useState<string[]>([]);
    const [languages, setLanguages] = useState<string[]>([]);
    const [subtitles, setSubtitles] = useState<string[]>([]);
    const [countries, setCountries] = useState<string[]>([]);
    const [description, setDescription] = useState('');
    const [techinfo, setTechinfo] = useState('');
    const [links, resetLinks] = useInput('');
    const [tags, setTags] = useState<number[]>([]);
    const [version, resetVersion] = useInput('0');
    const [imdb, resetIMDb] = useInput('');
    const [kinopoisk, resetKinopoisk] = useInput('');
    // const [rightAreas, setRightAreas] = useState<number | undefined>(undefined);
    const [rightAreas2, setRightAreas2] = useState<number[]>([]);
    // const [rightCats, setRightCats] = useState<number | undefined>(undefined);
    const [rightCats2, setRightCats2] = useState<number[]>([]);
    const [age, setAge] = useState<number | undefined>(undefined);
    const [timing, setTiming] = useState<number | undefined>(undefined);
    const [epnum, setEpnum] = useState<number | undefined>(undefined);
    const [vertical, setVertical] = useState(false);
    const [visibleToGuests, setVisibleToGuests] = useState<boolean>(true);
    const [trailers, setTrailers] = useState<number[]>([]);
    // const [hasS3Prefix, setHasS3Prefix] = useState(false);
    const [cardPrefix, setCardPrefix] = useState<string>('');
    const [cardBucket, setCardBucket] = useState<string>('');
    const [s3Client, setS3Client] = useState<S3Client | undefined>(undefined);
    const [s3Failed, setS3Failed] = useState(false);
    const myHttpHandler = useMemo(() => new MyHttpHandler(), []);

    const [admVisibleToGuests, setAdmVisibleToGuests] = useState<boolean>(false);
    const [admShowOnWelcome, setAdmShowOnWelcome] = useState<boolean>(false);
    const [admPromoOnWelcome, setAdmPromoOnWelcome] = useState<boolean>(false);

    const [viewPermissions, setViewPermissions] = useState<ACCESS_TYPES>(ACCESS_TYPES.ALL);
    const [downloadPermissions, setDownloadPermissions] = useState<ACCESS_TYPES>(ACCESS_TYPES.NOBODY);

    const [userIdViewPermissionList, setUserIdViewPermissionList] = useState<number[]>([]);
    const [userIdDownloadPermissionList, setUserIdDownloadPermissionList] = useState<number[]>([]);
    const [offerList, setOfferList] = useState<number[]>([]);
    const [availUsers, setAvailUsers] = useState<{
        value: number;
        label: string;
        custom: string | undefined;
    }[]>([])
    // const [media, setMedia] = useState<MediaFile[]>([]);

    const [availRightAreas, setAvailRightAreas] = useState<{ value: number; label: string; sys: boolean }[]>([]);
    const [availRightCats, setAvailRightCats] = useState<{ value: number; label: string; sys: boolean }[]>([]);

    const ALL_CATEGORY_ID = useMemo(() => {
        return availRightCats.filter((r) => r.label === ALL_CATEGORY).map((r) => r.value).at(0) || undefined;
    }, [availRightCats, ALL_CATEGORY]);

    const [titleChecked, setTitleChecked] = useState<boolean | null>(null);
    const [rightAreaChecked, setRightAreaChecked] = useState<boolean | null>(null);
    const [rightCatChecked, setRightCatChecked] = useState<boolean | null>(null);
    const [ageChecked, setAgeChecked] = useState<boolean | null>(null);
    const [timingChecked, setTimingChecked] = useState<boolean | null>(null);
    const [verticalChecked, setVerticalChecked] = useState<boolean | null>(null);
    const [epnumChecked, setEpnumChecked] = useState<boolean | null>(null);
    // const [viewPermissionChecked, setViewPermissionChecked] = useState<boolean | null>(null);
    // const [downloadPermissionChecked, setDownloadPermissionChecked] = useState<boolean | null>(null);
    // const [mediaChecked, setMediaChecked] = useState<(boolean | null)[]>([]);
    const [coverChecked, setCoverChecked] = useState<boolean | null>(null);
    const [trailerChecked, setTrailerChecked] = useState<boolean | null>(null);
    const [unitTypeChecked, setUnitTypeChecked] = useState<boolean | null>(null);
    const [countryChecked, setCountryChecked] = useState<boolean | null>(null);
    const [languageChecked, setLanguageChecked] = useState<boolean | null>(null);
    const [subtitleChecked, setSubtitleChecked] = useState<boolean | null>(null);
    const [genreChecked, setGenreChecked] = useState<boolean | null>(null);
    const [startYearChecked, setStartYearChecked] = useState<boolean | null>(null);
    const [descriptionChecked, setDescriptionChecked] = useState<boolean | null>(null);
    const [techinfoChecked, setTechinfoChecked] = useState<boolean | null>(null);
    const [qualityChecked, setQualityChecked] = useState<boolean | null>(null);
    const [tagChecked, setTagChecked] = useState<boolean | null>(null);
    const [collectionChecked, setCollectionChecked] = useState<boolean | null>(null);

    const [titleComment, setTitleComment] = useState<string | undefined>(undefined);
    const [rightAreaComment, setRightAreaComment] = useState<string | undefined>(undefined);
    const [rightCatComment, setRightCatComment] = useState<string | undefined>(undefined);
    const [ageComment, setAgeComment] = useState<string | undefined>(undefined);
    const [timingComment, setTimingComment] = useState<string | undefined>(undefined);
    const [epnumComment, setEpnumComment] = useState<string | undefined>(undefined);
    const [verticalComment, setVerticalComment] = useState<string | undefined>(undefined);
    // const [viewPermissionComment, setViewPermissionComment] = useState<string | undefined>(undefined);
    // const [downloadPermissionComment, setDownloadPermissionComment] = useState<string | undefined>(undefined);
    // const [mediaComment, setMediaComment] = useState<(string | undefined)[]>([]);
    const [coverComment, setCoverComment] = useState<string | undefined>(undefined);
    const [trailerComment, setTrailerComment] = useState<string | undefined>(undefined);
    const [unitTypeComment, setUnitTypeComment] = useState<string | undefined>(undefined);
    const [countryComment, setCountryComment] = useState<string | undefined>(undefined);
    const [languageComment, setLanguageComment] = useState<string | undefined>(undefined);
    const [subtitleComment, setSubtitleComment] = useState<string | undefined>(undefined);
    const [genreComment, setGenreComment] = useState<string | undefined>(undefined);
    const [startYearComment, setStartYearComment] = useState<string | undefined>(undefined);
    const [descriptionComment, setDescriptionComment] = useState<string | undefined>(undefined);
    const [techinfoComment, setTechinfoComment] = useState<string | undefined>(undefined);
    const [qualityComment, setQualityComment] = useState<string | undefined>(undefined);
    const [tagComment, setTagComment] = useState<string | undefined>(undefined);
    const [collectionComment, setCollectionComment] = useState<string | undefined>(undefined);

    const todayYear = new Date().getFullYear();
    const [availYears] = useState<{ value: number; label: string; }[]>(Array.from(new Array(todayYear - 1905), (val, index) => todayYear - index).map((value) => ({ value, label: value.toString() })));
    const [availGenres, setAvailGenres] = useState<{ value: number; name: string; }[]>([]);
    const [availLangs, setAvailLangs] = useState<{ value: number; name: string; }[]>([]);
    const [availCountries, setAvailCountries] = useState<{ value: number; name: string; }[]>([]);
    const [availSubtitles, setAvailSubtitles] = useState<{ value: number; name: string; }[]>([]);
    const [availTags, setAvailTags] = useState<{ value: number; label: string; }[]>([]);
    const [availCollections, setAvailCollections] = useState<{ value: number; label: string; }[]>([]);
    const [availAgeRestriction, setAvailAgeRestriction] = useState<{ value: number; label: string; }[]>([]);
    const [hasChanges, setHasChanges] = useState(false);
    const [unsavedCoverId, setUnsavedCoverId] = useState<number>(-1);
    const [unsavedTrailerId, setUnsavedTrailerId] = useState<number>(-1);
    // const [s3Trailer, setS3Trailer] = useState('');

    // const [availTrailers, setAvailTrailers] = useState<{ value: string; label: string; }[]>([]);

    // const [ignoreUpdate, setIgnoreUpdate] = useState(false);
    const ignoreUpdate = useRef(false);
    const ignoreRefresh = useRef(false);
    const [showSendModal, setShowSendModal] = useState(false);
    const [sendProviderReport, setSendProviderReport] = useState<boolean>(true);
    const [showTranscodeModal, setShowTranscodeModal] = useState(false);
    const [showTariffModal, setShowTariffModal] = useState(false);
    const [isOpenFolderSelection, setIsOpenFolderSelection] = useState(false);
    const [autoFill, setAutoFill] = useState<boolean>(false);

    const [allChecked, setAllChecked] = useState<boolean>(false);

    const [needNavigate, setNeedNavigate] = useState('');

    // hook'и в виде исключения
    const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(hasChanges || (createForm && !!unitId));

    const availStorageInTariff = useMemo(() => {
        const opts = tariff?.results.map((trf) => { return trf.options }).flat().filter((opt) => opt.label.startsWith('storage-') && opt.label !== 'storage-off') || [];
        const tOpts = tariff?.results.map((trf) => { return trf.tariffs.map(tf => tf.options).flat() }).flat().filter((opt) => opt.label.startsWith('storage-') && opt.label !== 'storage-off') || [];
        const allOpts = opts?.concat(tOpts);
        return isAdmin || allOpts.length > 0
    }, [tariff, isAdmin]);

    // effects
    useEffect(() => {
        if (!isAdmin) {
            getTempKey()
                .unwrap()
                .then((k) => {
                    const access = k.results;
                    fetchSdnConfig()
                        .unwrap()
                        .then((s3config) => {
                            const client = access && s3config?.results.enabled && createS3ClientWithMD5({
                                region: 'EU',
                                endpoint: s3config?.results.s3api,
                                forcePathStyle: s3config.results['access-type'] === 'path-style',
                                credentials: {
                                    accessKeyId: access?.accesskey,
                                    secretAccessKey: access?.secretkey,
                                    sessionToken: access?.token,
                                },
                                requestHandler: myHttpHandler,
                                requestChecksumCalculation: 'WHEN_REQUIRED',
                                apiVersion: 's3v4',
                            });
                            client && setS3Client(client);
                            if (data?.results.card_prefix) {
                                const pos = data.results.card_prefix.indexOf('/');
                                const bucket = data.results.card_prefix.slice(0, pos);
                                setCardBucket(bucket);
                                // const prefix = extractPrefix(data.results.system_prefixes.trailers);
                                // client && bucketObjectsList(bucket, prefix, client)
                                //     .then((list) => setAvailTrailers(
                                //         [{ value: '', label: t('cms:NOT_SET') }].concat(
                                //             list.Contents?.filter((c) => !c.Key?.includes('.card-system-folder'))
                                //                 .map((c) => ({ value: c.Key || '', label: c.Key || '' }))
                                //             || [])
                                //     )).catch(() => {
                                //         setS3Failed(true);
                                //     });
                            } else {
                                fetchSdnDisks()
                                    .unwrap()
                                    .then((disks) => {
                                        const myDisk = disks.results.find((d) => !d.shared);
                                        if (myDisk) {
                                            setCardBucket(myDisk.bucket);
                                        } else {
                                            setS3Failed(true);
                                        }
                                    })
                                    .catch(() => setS3Failed(true));
                            }
                        });
                })
                .catch(() => setS3Failed(true));
        }
    }, [isAdmin, data?.results.system_prefixes.trailers]);

    useEffect(() => { window.scrollTo(0, 0); }, []);

    useEffect(() => {
        setUnitId(parseInt(id || ''));
        setCreateForm(id === 'create');
        setEmptyTitle(false);
    }, [id]);

    useEffect(() => {
        setIsEdit(!isNaN(unitId));
    }, [unitId]);

    useEffect(() => {
        document.title = `${data?.results.title || t('Create a content unit')} | ${t('Mediabaza')}`;
    }, [t, data]);

    const showConfirm = useCallback(() => {
        setTimeout(() => {
            const ret = window.confirm(t('You have unsaved changes in content card. Continue?'));
            if (ret) {
                confirmNavigation();
            } else {
                cancelNavigation();
            }
        }, 1);
    }, [cancelNavigation, confirmNavigation, t]);

    useEffect(() => {
        if (showPrompt) {
            showConfirm();
        }
    }, [showConfirm, showPrompt]);

    useEffect(() => {
        if (auth.account) {
            fetchConsumers();
            fetchProviders();
        }
    }, [auth.account]);

    useEffect(() => {
        if (!isMounted) {
            return;
        }
        navigate(needNavigate);
    }, [needNavigate]);

    function clean() {
        setData(undefined);
        resetTitle();
        resetTitleEng();
        resetUnitType();
        setOwner(Number(auth.account));
        setQualities([]);
        setCollections([]);
        setStartyear(undefined);
        setGenres([]);
        setLanguages([]);
        setSubtitles([]);
        setCountries([]);
        setDescription('');
        setTechinfo('');
        resetLinks();
        resetIMDb();
        resetKinopoisk();
        setTags([]);
        setAge(undefined);
        setTiming(undefined);
        setVertical(false);
        setEpnum(undefined);
        setVisibleToGuests(true);
        resetVersion();
        setError('');
        // setMedia([]);
        setUserIdViewPermissionList([]);
        setUserIdDownloadPermissionList([]);
        setAutoFill(false);
        setViewPermissions(ACCESS_TYPES.ALL);
        setDownloadPermissions(ACCESS_TYPES.NOBODY);
        setHasChanges(false);
        setHasCover(false);
        setHasTrailer(false);
        // setHasS3Prefix(false);
        // setRightAreas(undefined);
        // setRightCats(undefined);
        setRightAreas2([]);
        setRightCats2([]);
        setOfferList([]);
        setCardPrefix('');
        setUnsavedCoverId(-1);
        setUnsavedTrailerId(-1);
        setTrailers([]);

        setTitleChecked(null);
        setRightAreaChecked(null);
        setRightCatChecked(null);
        setCoverChecked(null);
        setTrailerChecked(null);
        setUnitTypeChecked(null);
        setCountryChecked(null);
        setLanguageChecked(null);
        setSubtitleChecked(null);
        setGenreChecked(null);
        setStartYearChecked(null);
        setDescriptionChecked(null);
        setTechinfoChecked(null);
        setQualityChecked(null);
        setTagChecked(null);
        setCollectionChecked(null);

        setTitleComment(undefined);
        setRightAreaComment(undefined);
        setRightCatComment(undefined);
        setCoverComment(undefined);
        setTrailerComment(undefined);
        setUnitTypeComment(undefined);
        setCountryComment(undefined);
        setLanguageComment(undefined);
        setSubtitleComment(undefined);
        setGenreComment(undefined);
        setStartYearComment(undefined);
        setDescriptionComment(undefined);
        setTechinfoComment(undefined);
        setQualityComment(undefined);
        setTagComment(undefined);
        setCollectionComment(undefined);
    }

    const onClose = useCallback((ev: BeforeUnloadEvent) => {
        if (hasChanges || (createForm && unitId)) {
            ev.preventDefault();
            const msg = t('You have unsaved changes in content card. Continue?');
            ev.returnValue = msg;
            return msg;
        }
    }, [createForm, hasChanges, t, unitId]);

    // TODO управление модерацией
    const moderation = false;
    const isModerator = useCallback((r: string) => {
        return r === ROLE.SITE_ADMIN || r === ROLE.SITE_MODERATOR;
    }, [role]);

    useEffect(() => {
        window.addEventListener('beforeunload', onClose);

        return () => {
            window.removeEventListener('beforeunload', onClose);
        }
    }, [onClose]);

    // useEffect(() => {
    //     if (unitId === onlyMedia?.results?.id) {
    //         setMedia(onlyMedia?.results?.mediafiles || []);
    //     } else {
    //         setMedia([]);
    //     }
    //     onlyMedia?.results?.version && version.onChange(onlyMedia?.results?.version);
    //     // eslint-disable-next-line
    // }, [onlyMedia]);

    useEffect(() => {
        setViewPermissions(ACCESS_TYPES.ALL);
        setDownloadPermissions(ACCESS_TYPES.NOBODY);
    }, [id]);

    useEffect(() => {
        if (isEdit) {
            if (!ignoreRefresh.current) {
                fetchUnit(unitId);
            }
            // ignoreRefresh.current = false;
            // if (!isNaN(unitId)) {
            //     fetchMedia(unitId);
            // }
            // setIgnoreUpdate(false);
        }
        // eslint-disable-next-line
    }, [fetchUnit, isEdit, id]);

    useEffect(() => {
        const tmp = consumers?.results.data.reduce((acc, curr) => { acc[curr.id || 1] = { ...curr, username: curr.name, company: curr?.org?.name }; return acc; }, {} as { [id: number]: { id?: number, username?: string, company?: string } });
        setClientMap({ ...tmp });
    }, [consumers]);

    useEffect(() => {
        setAvailGenres(refBooks?.results.genre.map(g => ({ value: g.id, name: (g.displayname || '') })) || []);
        setAvailLangs(refBooks?.results.language.map(g => ({ value: g.id, name: (g.displayname || '') })) || []);
        setAvailCountries(refBooks?.results.country.map(g => ({ value: g.id, name: (g.displayname || '') })) || []);
        setAvailSubtitles(refBooks?.results.subtitle.map(g => ({ value: g.id, name: (g.displayname || '') })) || []);
        setAvailAgeRestriction(refBooks?.results.agerestriction.map(g => ({ value: g.id, label: g.displayname })) || []);
        setAvailCollections(refBooks?.results.collection.map(g => ({ value: g.id, label: g.displayname })) || []);
    }, [lang, refBooks]);

    useEffect(() => {
        setAvailRightCats(refUserBooks?.results.rightscat.map(g => ({ value: g.id, label: g.name, sys: g === null })) || []);
        setAvailRightAreas(refUserBooks?.results.rightsarea.map(g => ({ value: g.id, label: g.name, sys: g === null })) || []);
        setAvailTags(refUserBooks?.results.tag.filter((tag) => unitId || tag.owner_id === Number(auth.account)).map(g => ({ value: g.id, label: g.name })) || []);
    }, [refUserBooks, unitId]);

    useEffect(() => {
        if (unitId === rawData?.results?.id) {
            setData(rawData);
        }
    }, [rawData, unitId]);

    useEffect(() => {
        if (id === 'create' && !unitId) {
            clean();
        } else {
            if (data?.results && data.results.id === unitId) {
                if (!ignoreUpdate.current) {
                    title.onChange(data.results.title);
                    titleEng.onChange(data.results.title_eng || '');
                    unitType.onChange(data.results.unit_type?.id);
                    data.results.owner?.id && setOwner(data.results.owner?.id);
                    setQualities(data.results.qualities?.map(q => q.id) || []);
                    setCollections(data.results.collections?.map(c => c.id) || []);
                    setStartyear(data.results.startyear);
                    setGenres(data.results.genres?.map(g => g.id) || []);
                    setLanguages(data.results.languages?.map(g => g.id) || []);
                    setSubtitles(data.results.subtitles?.map(g => g.id) || []);
                    setCountries(data.results.countries?.map(g => g.id) || []);
                    setDescription(data.results.description || '');
                    setTechinfo(data.results.techinfo || '');
                    // setMedia(data.results.mediafiles || []);
                    links.onChange(JSON.stringify(data.results.links));
                    imdb.onChange(data.results.links.imdb)
                    kinopoisk.onChange(data.results.links.kinopoisk)
                    setTags(data.results.tags.map(tag => tag.id) || []);
                    version.onChange(data.results.version);
                    // setRightAreas(data.results.rights_area.at(0)?.id);
                    // setRightCats(data.results.rights_cat.at(0)?.id);
                    setRightAreas2(data.results.rights_area.map(r => r.id) || []);
                    setRightCats2(data.results.rights_cat.map(r => r.id) || []);
                    setAutoFill(data.results.autofill);
                    setAge(data.results.agerestriction?.id);
                    setTiming(data.results.timing);
                    setVertical(data.results.vertical);
                    setEpnum(data.results.epnum);
                    setCardPrefix(data.results.card_prefix ? extractPrefix(data.results.card_prefix) : '');

                    setVisibleToGuests(data.results.visible_to_guests);

                    // const missingSubtitles = (availSubtitles.length > 0 ? availSubtitles : undefined) || refBooks?.results.subtitle.map(g => ({ value: g.id, name: g.displayname || '' })) || [];
                    // subtitles.forEach(subtitle => !missingSubtitles.some(c => c.value === subtitle) ? missingSubtitles.push({ value: subtitle, name: subtitle }) : null);
                    // setAvailSubtitles(missingSubtitles);

                    setViewPermissions(data.results.view_access);
                    setDownloadPermissions(data.results.download_access);

                    setUserIdViewPermissionList(data.results.view_accesslist?.map(row => row.id || -1) || []);
                    setUserIdDownloadPermissionList(data.results.download_accesslist?.map(row => row.id || -1) || []);

                    setOfferList(data.results.offers?.map(u => u.id || -1) || []);
                    // setTrailers(data.results.trailers?.filter(tr => tr.prefix).map(tr => tr.prefix) || []);
                    setTrailers(data.results.trailers?.filter(tr => tr.id).map(tr => tr.id) || []);
                    // setS3Trailer(extractPrefix(data.results.trailers.at(0)?.prefix || ''));
                }
                setAdmPromoOnWelcome(data.results.adm_promo_on_welcome);
                setAdmShowOnWelcome(data.results.adm_show_on_welcome);
                setAdmVisibleToGuests(data.results.adm_visible_to_guests);

                setHasCover(data.results.covers?.length ? true : false);
                setHasTrailer(data.results.trailers?.length ? true : false);
                // setHasS3Prefix(data.results.card_prefix ? true : false);
                setCardPrefix(data.results.card_prefix ? extractPrefix(data.results.card_prefix) : '');
                version.onChange(data.results.version);

                // const checkmarks = data.results.moderation.checkmarks;
                // setTitleChecked(checkmarks.title.checked);
                // setRightAreaChecked(checkmarks.rights_area.checked);
                // setRightCatChecked(checkmarks.rights_cat.checked);
                // // setViewPermissionChecked()
                // // setDownloadPermissionChecked()
                // setCoverChecked(checkmarks.covers.checked);
                // setTrailerChecked(checkmarks.trailers.checked);
                // setUnitTypeChecked(checkmarks.unit_type.checked);
                // setCountryChecked(checkmarks.countries.checked);
                // setLanguageChecked(checkmarks.languages.checked);
                // setSubtitleChecked(checkmarks.subtitles.checked);
                // setGenreChecked(checkmarks.genres.checked);
                // setStartYearChecked(checkmarks.startyear.checked);
                // setDescriptionChecked(checkmarks.description.checked);
                // setTechinfoChecked(checkmarks.techinfo.checked);
                // setQualityChecked(checkmarks.qualities.checked);
                // setTagChecked(checkmarks.tags.checked);
                // setCollectionChecked(checkmarks.collections.checked);

                // const comments = data.results.moderation.comments;
                // setTitleComment(comments.unit?.comment);
                // setRightAreaComment(comments.rights_area?.comment);
                // setRightCatComment(comments.rights_cat?.comment);
                // // setViewPermissionComment()
                // // setDownloadPermissionComment()
                // setCoverComment(comments.covers?.comment);
                // setTrailerComment(comments.trailers?.comment);
                // setUnitTypeComment(comments.unit_type?.comment);
                // setCountryComment(comments.countries?.comment);
                // setLanguageComment(comments.languages?.comment);
                // setSubtitleComment(comments.subtitles?.comment);
                // setGenreComment(comments.genres?.comment);
                // setStartYearComment(comments.startyear?.comment);
                // setDescriptionComment(comments.description?.comment);
                // setTechinfoComment(comments.techinfo?.comment);
                // setQualityComment(comments.qualities?.comment);
                // setTagComment(comments.tags?.comment);
                // setCollectionComment(comments.collections?.comment);
            }
            // setIgnoreUpdate(false);
            ignoreUpdate.current = false;
        }
        // eslint-disable-next-line 
    }, [id, unitId, data]);

    useEffect(() => {
        if (
            titleChecked !== false
            && rightAreaChecked !== false
            && rightCatChecked !== false
            && unitTypeChecked !== false
            && qualityChecked !== false
            && collectionChecked !== false
            && startYearChecked !== false
            && genreChecked !== false
            && languageChecked !== false
            && subtitleChecked !== false
            && countryChecked !== false
            && descriptionChecked !== false
            && techinfoChecked !== false
            && tagChecked !== false
            && coverChecked !== false
            && trailerChecked !== false
        ) {
            setAllChecked(true);
        } else {
            setAllChecked(false);
        }
    }, [
        titleChecked, rightAreaChecked, rightCatChecked, unitTypeChecked,
        qualityChecked, collectionChecked, startYearChecked, genreChecked,
        languageChecked, subtitleChecked, countryChecked, descriptionChecked,
        techinfoChecked, tagChecked, coverChecked, trailerChecked
    ]);

    useEffect(() => {
        if (id === 'create') {
            if (title.value
                || unitType.value
                || qualities.length
                || tags.length
                || collections.length
                || startyear
                || rightAreas2.length
                || rightCats2.length
                || genres.length
                || languages.length
                || subtitles.length
                || countries.length
                || description
                || techinfo
                || age
                || timing
                || vertical
                || epnum
                || cardPrefix
                || !visibleToGuests
                || viewPermissions !== ACCESS_TYPES.ALL
                || downloadPermissions !== ACCESS_TYPES.NOBODY
                || userIdViewPermissionList.length
                || userIdDownloadPermissionList.length
                || offerList.length
                || (unsavedCoverId !== -1)
                || (unsavedTrailerId !== -1)
            ) {
                setHasChanges(true);
            } else {
                setHasChanges(false);
            }
            return;
        }
        if (data?.results) {
            // const comments = data.results.moderation.comments;
            // const checkmarks = data.results.moderation.checkmarks;
            if (title.value !== data.results.title
                || unitType.value !== (data.results.unit_type?.id || '')
                || owner !== data.results.owner?.id
                || JSON.stringify(qualities) !== JSON.stringify(data.results.qualities?.map(q => q.id))
                || JSON.stringify(tags) !== JSON.stringify(data.results.tags?.map(q => q.id) || [])
                || JSON.stringify(collections) !== JSON.stringify(data.results.collections?.map(g => g.id) || [])
                || (startyear || undefined) !== (data.results.startyear || undefined)
                || JSON.stringify(rightAreas2) !== JSON.stringify(data.results.rights_area?.map(g => g.id) || [])
                || JSON.stringify(rightCats2) !== JSON.stringify(data.results.rights_cat?.map(g => g.id) || [])
                || JSON.stringify(genres) !== JSON.stringify(data.results.genres?.map(g => g.id) || [])
                || JSON.stringify(languages) !== JSON.stringify(data.results.languages?.map(g => g.id) || [])
                || JSON.stringify(subtitles) !== JSON.stringify(data.results.subtitles?.map(g => g.id) || [])
                || JSON.stringify(countries) !== JSON.stringify(data.results.countries?.map(g => g.id) || [])
                || description !== (data.results.description || '')
                || techinfo !== (data.results.techinfo || '')
                || viewPermissions !== (data.results.view_access)
                || downloadPermissions !== (data.results.download_access)
                || age != data.results.agerestriction?.id
                || timing != data.results.timing
                || vertical !== data.results.vertical
                || epnum != data.results.epnum
                || visibleToGuests != data.results.visible_to_guests
                || JSON.stringify(userIdViewPermissionList) !== JSON.stringify(data.results.view_accesslist?.map(row => row.id || -1) || [])
                || JSON.stringify(userIdDownloadPermissionList) !== JSON.stringify(data.results.download_accesslist?.map(row => row.id || -1) || [])
                || JSON.stringify(offerList) !== JSON.stringify(data.results.offers?.map(u => u.id || -1) || [])
                // comments
                // || comments.unit?.comment !== titleComment
                // || comments.rights_area?.comment !== rightAreaComment
                // || comments.rights_cat?.comment !== rightCatComment
                // || comments.unit_type?.comment !== unitTypeComment
                // || comments.qualities?.comment !== qualityComment
                // || comments.collections?.comment !== collectionComment
                // || comments.startyear?.comment !== startYearComment
                // || comments.genres?.comment !== genreComment
                // || comments.languages?.comment !== languageComment
                // || comments.subtitles?.comment !== subtitleComment
                // || comments.countries?.comment !== countryComment
                // || comments.description?.comment !== descriptionComment
                // || comments.techinfo?.comment !== techinfoComment
                // || comments.tags?.comment !== tagComment
                // || comments.covers?.comment !== coverComment
                // || comments.trailers?.comment !== trailerComment
                // // checkmarks
                // || checkmarks.title?.checked !== titleChecked
                // || checkmarks.rights_area?.checked !== rightAreaChecked
                // || checkmarks.rights_cat?.checked !== rightCatChecked
                // || checkmarks.unit_type?.checked !== unitTypeChecked
                // || checkmarks.qualities?.checked !== qualityChecked
                // || checkmarks.collections?.checked !== collectionChecked
                // || checkmarks.startyear?.checked !== startYearChecked
                // || checkmarks.genres?.checked !== genreChecked
                // || checkmarks.languages?.checked !== languageChecked
                // || checkmarks.subtitles?.checked !== subtitleChecked
                // || checkmarks.countries?.checked !== countryChecked
                // || checkmarks.description?.checked !== descriptionChecked
                // || checkmarks.techinfo?.checked !== techinfoChecked
                // || checkmarks.tags?.checked !== tagChecked
                // || checkmarks.covers?.checked !== coverChecked
                // || checkmarks.trailers?.checked !== trailerChecked
                // autofill
                || data.results.autofill !== autoFill
                || (unsavedCoverId !== -1)
                || (unsavedTrailerId !== -1)
                || cardPrefix != (data.results.card_prefix ? extractPrefix(data.results.card_prefix) : '')
            ) {
                setHasChanges(true);
            } else {
                setHasChanges(false);
            }
        }
    }, [
        collections, techinfo, countries, data?.results, description, downloadPermissions, genres, languages, owner, epnum, cardPrefix, vertical,
        offerList, qualities, startyear, rightAreas2, rightCats2, subtitles, tags, title.value, unitType.value, age, timing,
        userIdDownloadPermissionList, userIdViewPermissionList, viewPermissions, autoFill, unsavedCoverId, unsavedTrailerId, visibleToGuests,
        titleComment, rightAreaComment, rightCatComment, unitTypeComment, qualityComment, collectionComment, startYearComment,
        genreComment, languageComment, subtitleComment, countryComment, descriptionComment, techinfoComment, tagComment, coverComment, trailerComment,
        titleChecked, rightAreaChecked, rightCatChecked, unitTypeChecked, qualityChecked, collectionChecked, startYearChecked,
        genreChecked, languageChecked, subtitleChecked, countryChecked, descriptionChecked, techinfoChecked, tagChecked, coverChecked, trailerChecked,
    ]);

    useEffect(() => {
        if (clientMap) {
            // const tmp = Object.values(clientMap).filter(u => u.username !== data?.results.owner?.username).map(u => ({ value: u.id || -1, label: `${u.username} [${u.company}]`, custom: u.username }));
            const tmp = Object.values(clientMap).filter(u => u.username !== data?.results.owner?.name).map(u => ({ value: u.id || -1, label: `${u.username} [${u.company}]`, custom: u.username }));
            tmp.sort((a, b) => {
                if (a.label < b.label) {
                    return -1;
                }
                if (a.label > b.label) {
                    return 1;
                }
                return 0;
            });
            setAvailUsers(tmp);
        }
    }, [data, clientMap]);

    const availAccounts = useMemo(() => {
        return providers?.results.data.map((a) => ({ value: a.id, label: a.name })) || [];
    }, [providers]);

    // function onMediaChange(titleFromFile?: string) {
    //     const draftTitle = title.value || titleFromFile;
    //     if (draftTitle) {
    //         title.onChange(draftTitle);
    //     }
    // }

    // async function createDraft(titleFromFile?: string) {
    //     // setIgnoreUpdate(true);
    //     const draftTitle = title.value || titleFromFile;
    //     if (draftTitle) {
    //         title.onChange(draftTitle);
    //     }
    //     const response = await createDraftRequest({ title: draftTitle, owner: owner }).unwrap();
    //     const createdUnitId = Number(response.results.id);
    //     ignoreRefresh.current = true;
    //     ignoreUpdate.current = true;
    //     setUnitId(createdUnitId);
    //     return createdUnitId;
    // }

    if (isConsumer(role.label)) {
        return <NotFoundPage />
    }

    // if (isLoading) {
    //     return <Spinner />
    // }

    // async function onApprove(e: React.MouseEvent<HTMLElement, MouseEvent>, setAction: (v: boolean) => void) {
    //     e.preventDefault();
    //     setAction(true);
    //     const requestData: Partial<ContentRequest> = {
    //         id: unitId,
    //     }
    //     if (isModerator(role.name)) {
    //         requestData.moderation = {
    //             checkmarks: {
    //                 unit: true,
    //             }
    //         }
    //     }
    //     editRequest(requestData).unwrap().then((response) => {
    //         setAction(false);
    //         if (myLocation.pathname === '/content/create') {
    //             setCreateForm(false);
    //             setHasChanges(false);
    //             setTimeout(() => navigate('/content/' + response.results.id), 1);
    //         }
    //     }).catch((err) => {
    //         setAction(false);
    //         setErrorSave(err);
    //         setErrorTitle(t('Save failed'));
    //         setError(extractErrorDetail(err));
    //     });
    // }

    const onCreateArea = useCallback(async (v: string) => {
        try {
            if (unitId) {
                const newcol = await createContentItem({ content_id: unitId, data: { typ: 'rightsarea', name: v, rank: 10 } }).unwrap();
                const first = newcol.results.filter((tag) => unitId || tag.owner_id === Number(auth.account)).at(0);
                if (first?.id) {
                    setRightAreas2([...rightAreas2, first.id]);
                }
            } else {
                const newcol = await createItem({ typ: 'rightsarea', name: v, rank: 10 }).unwrap();
                const first = newcol.results.filter((tag) => unitId || tag.owner_id === Number(auth.account)).at(0);
                if (first?.id) {
                    setRightAreas2([...rightAreas2, first.id]);
                }
            }
            refetchUserBooks();
            return true;
        } catch {
            return false;
        }
    }, [unitId, createContentItem, createItem, refetchUserBooks, rightAreas2, setRightAreas2]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onDeleteArea = useCallback(async (v: any) => {
        try {
            await deleteContentItem({ content_id: unitId, item_id: v });
            setRightAreas2(rightAreas2.filter(c => c !== v));
            refetchUserBooks();
            return true;
        } catch {
            return false;
        }
    }, [unitId, deleteContentItem, deleteItem, refetchUserBooks, rightAreas2, setRightAreas2]);

    const onCreateCat = useCallback(async (v: string) => {
        try {
            if (unitId) {
                const newcol = await createContentItem({ content_id: unitId, data: { typ: 'rightscat', name: v, rank: 10 } }).unwrap();
                const first = newcol.results.filter((tag) => unitId || tag.owner_id === Number(auth.account)).at(0);
                if (first?.id) {
                    setRightCats2([...rightCats2, first.id]);
                }
            } else {
                const newcol = await createItem({ typ: 'rightscat', name: v, rank: 10 }).unwrap();
                const first = newcol.results.filter((tag) => unitId || tag.owner_id === Number(auth.account)).at(0);
                if (first?.id) {
                    setRightCats2([...rightCats2, first.id]);
                }
            }
            refetchUserBooks();
            return true;
        } catch {
            return false;
        }
    }, [unitId, createContentItem, createItem, refetchUserBooks, rightCats2, setRightCats2]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onDeleteCat = useCallback(async (v: any) => {
        try {
            await deleteContentItem({ content_id: unitId, item_id: v });
            setRightCats2(rightCats2.filter(c => c !== v));
            refetchUserBooks();
            return true;
        } catch {
            return false;
        }
    }, [unitId, deleteContentItem, deleteItem, refetchUserBooks, rightCats2, setRightCats2]);

    const onCreateTag = useCallback(async (v: string) => {
        try {
            if (unitId) {
                const newtag = await createContentItem({ content_id: unitId, data: { typ: 'tag', name: v, rank: 10 } }).unwrap();
                const first = newtag.results.filter((tag) => unitId || tag.owner_id === Number(auth.account)).at(0);
                if (first?.id) {
                    setTags([...tags, first.id]);
                }
            } else {
                const newtag = await createItem({ typ: 'tag', name: v, rank: 10 }).unwrap();
                const first = newtag.results.filter((tag) => unitId || tag.owner_id === Number(auth.account)).at(0);
                if (first?.id) {
                    setTags([...tags, first.id]);
                }
            }
            refetchUserBooks();
            return true;
        } catch {
            return false;
        }
    }, [unitId, createContentItem, createItem, refetchUserBooks, tags, setTags]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onDeleteTag = useCallback(async (v: any) => {
        try {
            if (unitId) {
                await deleteContentItem({ content_id: unitId, item_id: v });
            } else {
                await deleteItem(v);
            }
            setTags(tags.filter(tag => tag !== v));
            refetchUserBooks();
            return true;
        } catch {
            return false;
        }
    }, [unitId, deleteContentItem, deleteItem, refetchUserBooks, tags, setTags]);

    // const onCreateCollection = useCallback(async (v: string) => {
    //     try {
    //         if (unitId) {
    //             const newcol = await createContentItem({ content_id: unitId, data: { typ: 'collection', name: v, rank: 10 } }).unwrap();
    //             const first = newcol.results.filter((col) => unitId || col.owner_id === Number(auth.account)).at(0);
    //             if (first?.id) {
    //                 setCollections([...collections, first.id]);
    //             }
    //         } else {
    //             const newcol = await createItem({ typ: 'collection', name: v, rank: 10 }).unwrap();
    //             const first = newcol.results.filter((col) => unitId || col.owner_id === Number(auth.account)).at(0);
    //             if (first?.id) {
    //                 setCollections([...collections, first.id]);
    //             }
    //         }
    //         refetchUserBooks();
    //         return true;
    //     } catch {
    //         return false;
    //     }
    // }, [unitId, createContentItem, createItem, refetchUserBooks, collections, setCollections]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // const onDeleteCollection = useCallback(async (v: any) => {
    //     try {
    //         if (unitId) {
    //             await deleteContentItem({ content_id: unitId, item_id: v });
    //         } else {
    //             await deleteItem(v);
    //         }
    //         setCollections(collections.filter(c => c !== v));
    //         refetchUserBooks();
    //         return true;
    //     } catch {
    //         return false;
    //     }
    // }, [unitId, deleteContentItem, deleteItem, refetchUserBooks, collections, setCollections]);

    async function onCreate(e: React.MouseEvent<HTMLElement, MouseEvent>, setAction: (v: boolean) => void, withApprove?: boolean) {
        e.preventDefault();
        if (!title.value) {
            setEmptyTitle(true);
            return;
        }
        setAction(true);
        setEmptyTitle(false);
        const action = !isEdit ? createRequest : editRequest;

        const requestData: ContentRequest = {
            title: title.value,
            title_eng: titleEng.value,
            unit_type: unitType.value,
            qualities: qualities,
            collections: collections.filter((col) => availCollections.find((ac) => ac.value === col)),
            startyear: startyear,
            genres: genres.map(gId => Number(gId)),
            languages: languages,
            subtitles: subtitles,
            countries: countries,
            description: description,
            techinfo: techinfo,
            tags: tags.filter((tag) => availTags.find((at) => at.value === tag)),
            links: { imdb: imdb.value, kinopoisk: kinopoisk.value },
            version: parseInt(version.value),
            view_access: viewPermissions,
            view_accesslist: userIdViewPermissionList,
            download_access: downloadPermissions,
            download_accesslist: userIdDownloadPermissionList,
            typ: data?.results.typ !== TYPS.CENSORED ? TYPS.NORMAL : undefined,
            offers: offerList.map(u => clientMap[u].id || -1),
            rights_area: rightAreas2,
            rights_cat: rightCats2,
            autofill: isModerator(role.label) ? undefined : autoFill,
            agerestriction: age,
            timing: timing,
            epnum: epnum,
            visible_to_guests: visibleToGuests,
            owner: owner,
            trailers: trailers,
            // http://127.0.0.1:9000/account7/kot-golova-glaz.jpg?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Content-Sha256=UNSIGNED-PAYLOAD&X-Amz-Credential=43RRTMS052HNNLBODGZE%2F20251110%2FEU%2Fs3%2Faws4_request&X-Amz-Date=20251110T095237Z&X-Amz-Expires=3600&X-Amz-Signature=14d169bc4e12ef3b4c02cbb3ff70e46d10cb7a777b4478cf308e7a7fe8901490&X-Amz-SignedHeaders=host&x-amz-checksum-mode=ENABLED&x-id=GetObject
            card_prefix: cardPrefix ? getCardPrefix(auth.account, cardPrefix) : (data?.results.card_prefix ? null : undefined),
            vertical: vertical,
        }
        if (isEdit) {
            requestData.id = unitId;
        }
        if (moderation && isModerator(role.label)) {
            requestData.moderation = {
                sendreport: sendProviderReport,
                comments: {
                    unit_type: unitTypeComment,
                    qualities: qualityComment,
                    collections: collectionComment,
                    startyear: startYearComment,
                    genres: genreComment,
                    languages: languageComment,
                    subtitles: subtitleComment,
                    countries: countryComment,
                    description: descriptionComment,
                    techinfo: techinfoComment,
                    tags: tagComment,
                    covers: coverComment,
                    trailers: trailerComment,
                    rights_area: rightAreaComment,
                    rights_cat: rightCatComment,
                    unit: titleComment,
                },
                checkmarks: {
                    unit: withApprove || undefined,
                    unit_type: withApprove || unitTypeChecked,
                    qualities: withApprove || qualityChecked,
                    collections: withApprove || collectionChecked,
                    startyear: withApprove || startYearChecked,
                    genres: withApprove || genreChecked,
                    languages: withApprove || languageChecked,
                    subtitles: withApprove || subtitleChecked,
                    countries: withApprove || countryChecked,
                    description: withApprove || descriptionChecked,
                    techinfo: withApprove || techinfoChecked,
                    tags: withApprove || tagChecked,
                    covers: withApprove || coverChecked,
                    trailers: withApprove || trailerChecked,
                    rights_area: withApprove || rightAreaChecked,
                    rights_cat: withApprove || rightCatChecked,
                    title: withApprove || titleChecked,
                }
            }
        }

        if (unsavedCoverId !== -1) {
            requestData.covers = [unsavedCoverId];
        }
        if (unsavedTrailerId !== -1) {
            requestData.trailers = [unsavedTrailerId];
        }
        action(requestData).unwrap().then((response) => {
            setAction(false);
            if (withApprove) {
                setCreateForm(false);
                setHasChanges(false);
                setNeedNavigate('/content');
                // setTimeout(() => navigate('/content'), 25);
            } else {
                if (myLocation.pathname === '/content/media/create') {
                    setCreateForm(false);
                    setHasChanges(false);
                    setNeedNavigate('/content/media/' + response.results.id);
                    // setTimeout(() => navigate('/content/' + response.results.id), 125);
                }
            }
            setUnsavedCoverId(-1);
            setUnsavedTrailerId(-1);
            fetchUnit(unitId);

            // if (unsavedCoverId !== -1 && response.results.id) {
            //     attachFile({ id: response.results.id, file_id: unsavedCoverId, file_type: 'cover' }).unwrap()
            //         .then(() => {
            //             setUnsavedCoverId(-1);
            //             fetchUnit(unitId);
            //         })
            //         .catch((err) => {
            //             setAction(false);
            //             setErrorSave(err);
            //             setErrorTitle(t('Save failed'));
            //             setError(extractErrorDetail(err));
            //         })
            // }
            // if (unsavedTrailerId !== -1 && response.results.id) {
            //     attachFile({ id: response.results.id, file_id: unsavedTrailerId, file_type: 'trailer' }).unwrap()
            //         .then(() => {
            //             setUnsavedTrailerId(-1);
            //             fetchUnit(unitId);
            //         })
            //         .catch((err) => {
            //             setAction(false);
            //             setErrorSave(err);
            //             setErrorTitle(t('Save failed'));
            //             setError(extractErrorDetail(err));
            //         })
            // }
            setCreateForm(false);
            setHasChanges(false);
        }).catch((err) => {
            setAction(false);
            setErrorSave(err);
            setErrorTitle(t('Save failed'));
            setError(extractErrorDetail(err));
        });
    }

    if (contentError) {
        if ((contentError as FetchBaseQueryError).status === 404) {
            return <NotFoundPage />
        } else {
            return <div>{JSON.stringify(contentError)}</div>
        }
    }

    return (
        <Content>
            <SendModal isOpen={showSendModal} onClose={() => setShowSendModal(false)} unitId={unitId} unitTitle={title.value} />
            <ErrorModal isOpen={!!error} onClose={() => setError('')} content={error} title={errorTitle} />
            <TranscodeModal
                profileId={transProfiles?.results.data.at(0)?.id || 1}
                mediaUrls={data?.results.mediafiles?.map(m => m.url) || []}
                isOpen={showTranscodeModal}
                onClose={() => setShowTranscodeModal(false)}
            />
            <Modal isOpen={showTariffModal} onClose={() => setShowTariffModal(false)}>
                <div className="form-message">
                    <h1 className="content-add__send-heading">
                        <Trans>content:SERVICE_NOT_SUPPORT</Trans>
                    </h1>
                    <br />
                    <div className="content-add__send-submit">
                        <span className={'btn'} onClick={() => {
                            navigate('/tariff');
                            setShowTariffModal(false);
                        }}>
                            <Trans>content:CHANGE_TARIFF</Trans>
                        </span>
                    </div>
                </div>
            </Modal>
            {isOpenFolderSelection && s3Client && <StorageFolderSelection
                client={s3Client}
                bucketname={cardBucket}
                isOpen={isOpenFolderSelection}
                onClose={() => setIsOpenFolderSelection(false)}
                prefix={[]}
                mode={'none'}
                onAction={(p) => {
                    setCardPrefix(p);
                    setIsOpenFolderSelection(false);
                }}
            />}

            <Form action="#" className="content-add">
                {id === 'create' ?
                    <Header1>
                        <Trans>Create a content unit</Trans>
                    </Header1>
                    :
                    <>
                        <Header1 title={data?.results.title}>
                            {data?.results.title}
                        </Header1>
                        {!!unitId && <div className="permission">
                            <div className="user-settings__labels">
                                <span className="user-settings__labels-item"><Trans>Content unit</Trans> ID{data?.results.id}</span>
                            </div>
                        </div>}
                        {isModerator(role.label) && data?.results.autofill &&
                            <p className="content-add__fill">
                                <Edit />
                                {' '}
                                <Trans>tv:FILL_OUT</Trans>
                            </p>
                        }
                        {data?.results.typ === TYPS.CENSORED ?
                            <div className="content-add__status _error"><Trans>tv:BLOCKED</Trans></div>
                            :
                            <>
                                {data?.results.moderated === true && <div className="content-add__status _ok"><Trans>tv:STATUS_CHECKED</Trans></div>}
                                {data?.results.moderated === false && <div className="content-add__status _error"><Trans>tv:STATUS_ERROR</Trans></div>}
                                {/* TODO следующий релиз */}
                                {/* {data?.results.moderated === null && <div className="content-add__status">На проверке</div>} */}
                            </>
                        }
                    </>
                }

                <ContentAddBody>
                    <ContentLeftSideSticky>
                        <ContentLeftSideItem>
                            <Link to={'/help?#how-to-create-media'}>
                                <Trans>Instructions</Trans>
                            </Link>
                        </ContentLeftSideItem>
                        {id !== 'create' &&
                            <>
                                <ContentLeftSideItem>
                                    <Link to={`/content/view/${id}`}><Trans>tv:VIEW_AS_CONSUMER</Trans></Link>
                                </ContentLeftSideItem>
                                <ContentLeftSideItem>
                                    <Link to={`/content/publicview/${id}?type=media`}><Trans>tv:VIEW_AS_GUEST</Trans></Link>
                                </ContentLeftSideItem>
                                {(role.label === ROLE.SITE_ADMIN || isProvider(role.label)) &&
                                    <ContentLeftSideItem>
                                        <a href="#send" onClick={(e) => {
                                            e.preventDefault();
                                            setShowTariffModal(true);
                                        }}><Trans>tv:QUICK_SEND</Trans></a>
                                    </ContentLeftSideItem>
                                }
                                {(role.label === ROLE.SITE_ADMIN || isProvider(role.label)) &&
                                    <ContentLeftSideItem>
                                        <a href="#transcode" onClick={(e) => {
                                            e.preventDefault();
                                            setShowTariffModal(true);
                                        }}><Trans>Transcode</Trans></a>
                                    </ContentLeftSideItem>
                                }
                                <ContentLeftSideItem>
                                    <a href="" onClick={(e) => {
                                        e.preventDefault();
                                        const tomorrow = new Date();
                                        tomorrow.setDate(tomorrow.getDate() + 3);
                                        fetchCreatePublicLink({ unit: unitId, expiry: toMplatBackendDate(tomorrow) as string }).unwrap()
                                            .then((d) => {
                                                alert.info((<>
                                                    <a href={d.results.url} target="_blank"><Trans>Link</Trans></a> <Copy style={{ cursor: 'pointer' }} onClick={() => navigator.clipboard.writeText(d.results.url)} />
                                                </>), t('content:PUBLIC_LINK_TITLE'));
                                            })
                                            .catch((err) => alert.error(extractErrorDetail(err)));
                                    }}>
                                        <Trans>content:PUBLIC_LINK_DAYLY</Trans>
                                    </a>
                                </ContentLeftSideItem>

                                <ContentLeftSideItem>
                                    <Link to={`/content/history/${id}`} target="_blank">
                                        <Trans>tv:HISTORY</Trans>
                                    </Link>
                                </ContentLeftSideItem>
                                {(role.label === ROLE.SITE_ADMIN || isProvider(role.label)) &&
                                    <ContentLeftSideItem>
                                        <a href="#delete-content" className="_remove" onClick={(e) => {
                                            e.preventDefault();
                                            const ret = window.confirm(t('Are you sure you want to delete this content unit?'));
                                            if (ret) {
                                                deleteRequest(unitId).unwrap()
                                                    .then(() => {
                                                        setHasChanges(false);
                                                        setNeedNavigate('/content');
                                                        // setTimeout(() => navigate('/content', { replace: true }), 25);
                                                    })
                                                    .catch((err) => {
                                                        setErrorTitle(t('Delete failed'));
                                                        setError(extractErrorDetail(err));
                                                    });
                                            }
                                        }}>
                                            <Trans>tv:DELETE_CARD</Trans>
                                        </a>
                                    </ContentLeftSideItem>
                                }
                                {isModerator(role.label) && data?.results.typ !== TYPS.CENSORED &&
                                    <ContentLeftSideItem>
                                        <a href="#censored-content" className="_remove" onClick={(e) => {
                                            e.preventDefault();
                                            const ret = window.confirm(t('Are you sure you want to lock this content unit?'));
                                            if (ret) {
                                                makeCensoredRequest(unitId).unwrap()
                                                    .then(() => {
                                                        setHasChanges(false);
                                                        fetchUnit(unitId);
                                                        // setTimeout(() => navigate('/content', { replace: true }), 1);
                                                    })
                                                    .catch((err) => {
                                                        setErrorTitle(t('Delete failed'));
                                                        setError(extractErrorDetail(err));
                                                    });
                                            }
                                        }}>
                                            <Trans>tv:BLOCK_CARD</Trans>
                                        </a>
                                    </ContentLeftSideItem>
                                }
                                {isModerator(role.label) && data?.results.typ === TYPS.CENSORED &&
                                    <ContentLeftSideItem>
                                        <a href="#uncensored-content" className="status-ok" onClick={(e) => {
                                            e.preventDefault();
                                            const ret = window.confirm(t('Are you sure you want to unlock this content unit?'));
                                            if (ret) {
                                                unmakeCensoredRequest(unitId).unwrap()
                                                    .then(() => {
                                                        setHasChanges(false);
                                                        fetchUnit(unitId);
                                                        // setTimeout(() => navigate('/content', { replace: true }), 1);
                                                    })
                                                    .catch((err) => {
                                                        setErrorTitle(t('Delete failed'));
                                                        setError(extractErrorDetail(err));
                                                    });
                                            }
                                        }}>
                                            <Trans>tv:UNBLOCK_CARD</Trans>
                                        </a>
                                    </ContentLeftSideItem>
                                }
                            </>
                        }
                    </ContentLeftSideSticky>

                    <Section label={t('Enter a title')} hint={`· ${t('required')}`}>
                        <FormEntryFull
                            moderation={moderation}
                            editable={isModerator(role.label)}
                            checked={titleChecked}
                            onCheckedChange={(v) => setTitleChecked(v)}
                            comment={titleComment}
                            onCommentChange={(v) => setTitleComment(v)}
                        >
                            <TextInput data-test-id="contentTitleInput" {...title} placeholder={t('Title')} required error={(errorSave?.detail?.non_field_errors || emptyTitle) && t('Enter a title')} />
                        </FormEntryFull>
                    </Section>
                    <Section label={t('Type of rights')}>
                        <Label>{t('Territory')}</Label>
                        <FormEntryFull
                            moderation={moderation}
                            editable={isModerator(role.label)}
                            checked={rightAreaChecked}
                            onCheckedChange={(v) => setRightAreaChecked(v)}
                            comment={rightAreaComment}
                            onCommentChange={(v) => setRightAreaComment(v)}
                        >
                            <MultiSelect
                                dataTestId="contentRightAreasMultiSelect"
                                value={rightAreas2}
                                options={availRightAreas}
                                setValue={setRightAreas2}
                                placeholder={t('Country')}
                                createHeader={t('content:CREATE_NEW_COUNTRY')}
                                createPlaceholder={t('content:ENTER_COUNTRY_NAME')}
                                // editable
                                onCreate={onCreateArea}
                                onDelete={onDeleteArea}
                            />
                        </FormEntryFull>
                        <Label>{t('Category')}</Label>
                        <FormEntryFull
                            moderation={moderation}
                            editable={isModerator(role.label)}
                            checked={rightCatChecked}
                            onCheckedChange={(v) => setRightCatChecked(v)}
                            comment={rightCatComment}
                            onCommentChange={(v) => setRightCatComment(v)}
                        >
                            <MultiSelect
                                dataTestId="contentRightAreasMultiSelect"
                                value={rightCats2}
                                options={availRightCats}
                                setValue={(v) => {
                                    if (ALL_CATEGORY_ID) {
                                        if (rightCats2.includes(ALL_CATEGORY_ID)) {
                                            if (v.length > 1) {
                                                setRightCats2(v.filter((r) => r !== ALL_CATEGORY_ID));
                                            } else {
                                                setRightCats2(v);
                                            }
                                        } else {
                                            if (v.includes(ALL_CATEGORY_ID)) {
                                                setRightCats2([ALL_CATEGORY_ID]);
                                            } else {
                                                setRightCats2(v);
                                            }
                                        }
                                    } else {
                                        setRightCats2(v);
                                    }
                                }}
                                placeholder={t('Category')}
                                createHeader={t('content:CREATE_NEW_RIGHTS')}
                                createPlaceholder={t('content:ENTER_RIGHTS_NAME')}
                                // editable
                                onCreate={onCreateCat}
                                onDelete={onDeleteCat}
                            />
                        </FormEntryFull>
                    </Section>
                    <Section label={t('Owner')}>
                        <FormEntryFull>
                            <Select
                                disabled
                                className="form__item"
                                value={owner}
                                options={availAccounts}
                                placeholder={'Choose a owner'}
                                onChange={(v) => setOwner(v)}
                            />
                        </FormEntryFull>
                    </Section>

                    <Section label={t('Vertical content')}>
                        <FormEntryFull
                            moderation={moderation}
                            editable={isModerator(role.label)}
                            checked={verticalChecked}
                            onCheckedChange={(v) => setVerticalChecked(v)}
                            comment={verticalComment}
                            onCommentChange={(v) => setVerticalComment(v)}
                        >
                            <SelectButton
                                value={vertical}
                                onChange={(v) => setVertical(v)}
                                items={[{ value: true, label: t('Yes') }, { value: false, label: t('No') }]}
                            />
                        </FormEntryFull>
                    </Section>

                    <Section label={t('Load logo or cover')} >
                        <UnitCoverUploaderV1
                            has={hasCover}
                            label={t('Load logo or cover')}
                            accept="image/*"
                            url={data?.results?.covers?.at(0)?.url}
                            account_id={data?.results.owner?.id || Number(auth.account)}
                            delete_url={`/be/api/v1/content/media/${unitId}/cover/${data?.results?.covers?.at(0)?.id}/`}
                            onChange={(file_id) => { ignoreUpdate.current = true; setUnsavedCoverId(file_id); if (unitId) fetchUnit(unitId); }}
                            checked={coverChecked}
                            setChecked={setCoverChecked}
                            comment={coverComment}
                            setComment={setCoverComment}
                            moderateable={isModerator(role.label)}
                            moderation={moderation}
                            cropper
                            cropperAspectRatio={vertical ? 9 / 16 : 16 / 9}
                        />
                    </Section>

                    <Section label={t('Load demo')} >
                        {/* <div className="form__item">
                            <a href={`/sdn/my/${encodeURIComponent(data.results.system_prefixes.trailers.slice(data.results.system_prefixes.trailers.indexOf('/') + 1))}`} target="_blank">
                                <Button>
                                    <Trans>Show on Disk</Trans>
                                </Button>
                            </a>
                        </div>
                        <Select value={s3Trailer} options={availTrailers} onChange={(v) => setS3Trailer(v)} />
                        {s3Client && <S3TrailerUploader
                            onSuccess={() => {
                                bucketObjectsList(cardBucket, extractPrefix(data.results.system_prefixes.trailers), s3Client)
                                    .then((list) => setAvailTrailers(
                                        [{ value: '', label: t('cms:NOT_SET') }].concat(
                                            list.Contents?.filter((c) => !c.Key?.includes('.card-system-folder'))
                                                .map((c) => ({ value: c.Key || '', label: c.Key || '' }))
                                            || [])
                                    ));
                            }}
                            client={s3Client}
                            myHttpHandler={myHttpHandler}
                            bucket={cardBucket}
                            prefix={extractPrefix(data.results.system_prefixes.trailers)}
                        />
                        } */}

                        <UnitTrailerUploaderV1
                            has={hasTrailer}
                            account_id={data?.results.owner?.id || Number(auth.account)}
                            label={t('Load demo')}
                            accept={AVAIL_VIDEO_FORMATS.join(',')}
                            show_url={data?.results.trailers?.at(0)?.signedurl}
                            delete_url={`/be/api/v1/content/media/${unitId}/trailer/${data?.results?.trailers?.at(0)?.id}/`}
                            onChange={(file_id) => { ignoreUpdate.current = true; setUnsavedTrailerId(file_id); if (unitId) setTimeout(() => fetchUnit(unitId), 1000); }}
                            checked={trailerChecked}
                            setChecked={setTrailerChecked}
                            comment={trailerComment}
                            setComment={setTrailerComment}
                            moderateable={isModerator(role.label)}
                            moderation={moderation}
                        // blocked={!hasS3Prefix}
                        // blockedMsg={!hasS3Prefix && <strong><Trans>Для загрузки данных в карточку, укажите "Путь S3" в соответствующем поле</Trans></strong>}
                        // blocked={!(auth.v2fa === 'true' || auth.v2fa === true)}
                        // blockedMsg={(!(auth.v2fa === 'true' || auth.v2fa === true)) ?
                        //     <>
                        //         <strong>
                        //             <Trans>Two-factor authentication must be enabled to download</Trans>.
                        //             <br />
                        //         </strong>
                        //         <Trans i18nKey="2FAConfigureLink">
                        //             To set up, follow the <Link to="/otp" className="link-accent">link</Link>
                        //         </Trans>
                        //     </>
                        //     : ''
                        // }
                        />
                    </Section>

                    {role.label !== ROLE.SITE_MODERATOR &&
                        <>
                            <Section label={t('Accessibility mode for view')}>
                                <FormEntryFull>
                                    <Permissioner
                                        label=""
                                        placeholder={t('Choose a customers')}
                                        selectLabel={t('Choose a customers to view')}
                                        value={viewPermissions}
                                        setValue={(v) => { setViewPermissions(v); if (v === ACCESS_TYPES.NOBODY) { setVisibleToGuests(false); } }}
                                        availUsers={availUsers}
                                        userList={userIdViewPermissionList}
                                        setUserList={setUserIdViewPermissionList}
                                    />
                                </FormEntryFull>
                                <h3 style={{ color: viewPermissions === ACCESS_TYPES.NOBODY ? 'var(--c-text-grey)' : undefined }}>
                                    <Trans>Show to unregistered users</Trans>
                                    {' '}
                                    <InputSwitch disabled={viewPermissions === ACCESS_TYPES.NOBODY} value={visibleToGuests} onChange={(v) => setVisibleToGuests(v)} label={undefined} />
                                </h3>
                            </Section>
                            {/* <Section label={t('Accessibility mode for download')}>
                                <FormEntry>
                                    <Permissioner
                                        data-test-id="contentDownloadPermission"
                                        label=""
                                        placeholder={t('Choose a customers')}
                                        selectLabel={t('Choose a customers to download')}
                                        value={downloadPermissions}
                                        setValue={setDownloadPermissions}
                                        availUsers={availUsers}
                                        userList={userIdDownloadPermissionList}
                                        setUserList={setUserIdDownloadPermissionList}
                                    />
                                </FormEntry>
                            </Section> */}
                        </>
                    }

                    <Section label={t('Age')}>
                        <FormEntryFull
                            moderation={moderation}
                            editable={isModerator(role.label)}
                            checked={ageChecked}
                            onCheckedChange={(v) => setAgeChecked(v)}
                            comment={ageComment}
                            onCommentChange={(v) => setAgeComment(v)}
                        >
                            <Select
                                className="form__item"
                                value={age}
                                options={availAgeRestriction}
                                placeholder={t('Choose a agerestriction')}
                                onChange={(v) => setAge(v)}
                            />
                        </FormEntryFull>
                    </Section>

                    <Section label={t('Duration, min')}>
                        <FormEntryFull
                            moderation={moderation}
                            editable={isModerator(role.label)}
                            checked={timingChecked}
                            onCheckedChange={(v) => setTimingChecked(v)}
                            comment={timingComment}
                            onCommentChange={(v) => setTimingComment(v)}
                        >
                            <NumberInput
                                min={0}
                                value={timing}
                                placeholder={t('Set a duration')}
                                onChange={(e) => setTiming(Number(e.target.value))}
                            />
                        </FormEntryFull>
                    </Section>

                    <Section label={t('Number of series')}>
                        <FormEntryFull
                            moderation={moderation}
                            editable={isModerator(role.label)}
                            checked={epnumChecked}
                            onCheckedChange={(v) => setEpnumChecked(v)}
                            comment={epnumComment}
                            onCommentChange={(v) => setEpnumComment(v)}
                        >
                            <NumberInput
                                min={0}
                                value={epnum}
                                placeholder={t('Set a number of series')}
                                onChange={(e) => setEpnum(Number(e.target.value))}
                            />
                        </FormEntryFull>
                    </Section>

                    <Section label={t('Meta')} hint={`· ${t('optional')}`}>
                        <Label>{t('Category')}</Label>
                        <FormEntryFull
                            moderation={moderation}
                            editable={isModerator(role.label)}
                            checked={unitTypeChecked}
                            onCheckedChange={(v) => setUnitTypeChecked(v)}
                            comment={unitTypeComment}
                            onCommentChange={(v) => setUnitTypeComment(v)}
                        >
                            <Select
                                dataTestId="contentCategorySelect"
                                value={unitType.value}
                                options={refBooks?.results.utype.map(g => ({ value: g.id, label: g.displayname })) || []}
                                onChange={(v: string) => unitType.onChange(v)}
                            />
                        </FormEntryFull>
                        <Label>{t('Country of Origin')}</Label>
                        <FormEntryFull
                            moderation={moderation}
                            editable={isModerator(role.label)}
                            checked={countryChecked}
                            onCheckedChange={(v) => setCountryChecked(v)}
                            comment={countryComment}
                            onCommentChange={(v) => setCountryComment(v)}
                        >
                            <MultiSelect dataTestId="contentCountriesMultiSelect" value={countries} options={availCountries.map(c => ({ value: c.value, label: c.name }))} setValue={setCountries} placeholder={t('Choose a country')} />
                        </FormEntryFull>
                        <Label>{t('Voice language')}</Label>
                        <FormEntryFull
                            moderation={moderation}
                            editable={isModerator(role.label)}
                            checked={languageChecked}
                            onCheckedChange={(v) => setLanguageChecked(v)}
                            comment={languageComment}
                            onCommentChange={(v) => setLanguageComment(v)}
                        >
                            <MultiSelect dataTestId="contentLanguagesMultiSelect" value={languages} options={availLangs.map(c => ({ value: c.value, label: c.name }))} setValue={setLanguages} placeholder={t('Choose a language')} />
                        </FormEntryFull>
                        <Label>{t('Subtitle')}</Label>
                        <FormEntryFull
                            moderation={moderation}
                            editable={isModerator(role.label)}
                            checked={subtitleChecked}
                            onCheckedChange={(v) => setSubtitleChecked(v)}
                            comment={subtitleComment}
                            onCommentChange={(v) => setSubtitleComment(v)}
                        >
                            <MultiSelect dataTestId="contentSubtitlesMultiSelect" value={subtitles} options={availSubtitles.map(c => ({ value: c.value, label: c.name }))} setValue={setSubtitles} placeholder={t('Choose a language')} />
                        </FormEntryFull>
                        <Label>{t('Genre')}</Label>
                        <FormEntryFull
                            moderation={moderation}
                            editable={isModerator(role.label)}
                            checked={genreChecked}
                            onCheckedChange={(v) => setGenreChecked(v)}
                            comment={genreComment}
                            onCommentChange={(v) => setGenreComment(v)}
                        >
                            <MultiSelect dataTestId="contentGenresMultiSelect" value={genres} options={availGenres.map(c => ({ value: c.value, label: c.name }))} setValue={setGenres} placeholder={t('Choose a genre')} />
                        </FormEntryFull>
                        <Label>{t('Year of issue')}</Label>
                        <FormEntryFull
                            moderation={moderation}
                            editable={isModerator(role.label)}
                            checked={startYearChecked}
                            onCheckedChange={(v) => setStartYearChecked(v)}
                            comment={startYearComment}
                            onCommentChange={(v) => setStartYearComment(v)}
                        >
                            <Select dataTestId="contentYearSelect" value={startyear} placeholder={t('Choose a year')} options={availYears} onChange={(v) => setStartyear(v)} />
                        </FormEntryFull>
                        <Label>{t('Description')}</Label>
                        <FormEntryFull
                            moderation={moderation}
                            editable={isModerator(role.label)}
                            checked={descriptionChecked}
                            onCheckedChange={(v) => setDescriptionChecked(v)}
                            comment={descriptionComment}
                            onCommentChange={(v) => setDescriptionComment(v)}
                        >
                            <TextArea dataTestId="contentDescription" value={description} placeholder={t('Enter a description')} onChange={e => setDescription(e.target.value)} />
                        </FormEntryFull>
                        <Label>{t('Techinfo')}</Label>
                        <FormEntryFull
                            moderation={moderation}
                            editable={isModerator(role.label)}
                            checked={techinfoChecked}
                            onCheckedChange={(v) => setTechinfoChecked(v)}
                            comment={techinfoComment}
                            onCommentChange={(v) => setTechinfoComment(v)}
                        >
                            <TextArea dataTestId="contentTechinfo" value={techinfo} placeholder={t('Enter a techinfo')} onChange={e => setTechinfo(e.target.value)} />
                        </FormEntryFull>
                        <Label>{t('Quality')}</Label>
                        <FormEntryFull
                            moderation={moderation}
                            editable={isModerator(role.label)}
                            checked={qualityChecked}
                            onCheckedChange={(v) => setQualityChecked(v)}
                            comment={qualityComment}
                            onCommentChange={(v) => setQualityComment(v)}
                        >
                            <MultiSelect dataTestId="contentQualityMultiSelect" value={qualities} options={refBooks?.results.quality.map(g => ({ value: g.id, label: g.displayname })) || []} setValue={setQualities} placeholder={t('Choose a quality')} />
                        </FormEntryFull>
                        <Label>{t('Tags')}</Label>
                        <FormEntryFull
                            moderation={moderation}
                            editable={isModerator(role.label)}
                            checked={tagChecked}
                            onCheckedChange={(v) => setTagChecked(v)}
                            comment={tagComment}
                            onCommentChange={(v) => setTagComment(v)}
                        >
                            <MultiSelect
                                dataTestId="contentTagsMultiSelect"
                                value={tags}
                                options={availTags}
                                setValue={setTags}
                                placeholder={t('Tags')}
                                editable
                                deletable={role.label !== ROLE.SITE_MODERATOR}
                                createHeader={t('content:CREATE_NEW_TAG')}
                                createPlaceholder={t('content:ENTER_TAG_NAME')}
                                onCreate={onCreateTag}
                                onDelete={onDeleteTag}
                            />
                        </FormEntryFull>
                        <Label>{t('Choose a collection')}</Label>
                        <FormEntryFull
                            moderation={moderation}
                            editable={isModerator(role.label)}
                            checked={collectionChecked}
                            onCheckedChange={(v) => setCollectionChecked(v)}
                            comment={collectionComment}
                            onCommentChange={(v) => setCollectionComment(v)}
                        >
                            <MultiSelect
                                dataTestId="contentCollectionMultiSelect"
                                value={collections}
                                options={availCollections}
                                setValue={setCollections}
                                placeholder={t('Collection')}
                            // createHeader={t('content:CREATE_NEW_COLLECTION')}
                            // createPlaceholder={t('content:ENTER_COLLECTION_NAME')}
                            // editable
                            // deletable={role.label !== ROLE.SITE_MODERATOR}
                            // onCreate={onCreateCollection}
                            // onDelete={onDeleteCollection}
                            />
                        </FormEntryFull>
                    </Section>

                    <Section label={t('Card files')} hint={`· ${t('optional')}`}>
                        {(!otp.otp_enabled) ?
                            <div className="form__item" style={{ fontSize: '0.9rem' }}>
                                <strong>
                                    <Trans>To use Disk, two-factor authentication must be enabled</Trans>.
                                    <br />
                                </strong>
                                <Trans i18nKey="2FAConfigureLink">
                                    To set up, follow the <Link to="/otp" className="link-accent">link</Link>
                                </Trans>
                            </div>
                            : <FormEntryFull
                                moderation={false}
                                editable={false}
                            >
                                {!isAdmin ? (
                                    availStorageInTariff && myAcc.disks.length > 0 ? <>
                                        <Label><Trans>S3 path</Trans>: {cardPrefix ? <b>{cardPrefix}</b> : <i><Trans>cms:NOT_SET</Trans></i>}</Label>
                                        <div className="storage__popup-cta">
                                            <Button onClick={() => setIsOpenFolderSelection(true)}>
                                                <Trans>Choose</Trans>
                                            </Button>
                                            {cardPrefix && (
                                                <button type="button" className="storage__popup-share-remove" onClick={() => setCardPrefix('')} >
                                                    <Trans>Delete</Trans>
                                                </button>
                                            )}
                                            {data?.results.card_prefix && cardPrefix !== extractPrefix(data?.results.card_prefix) && (
                                                <button type="button" className="storage__popup-share-remove" style={{ color: 'var(--c-btn)' }} onClick={() => setCardPrefix(extractPrefix(data.results.card_prefix || ''))} >
                                                    <Trans>Revert</Trans>
                                                </button>
                                            )}
                                        </div>
                                    </>
                                        :
                                        <>
                                            <Label>
                                                <Trans>В вашем тарифе не предусмотрен доступ в Мдиск.</Trans>
                                            </Label>
                                            <Button onClick={() => navigate('/tariff')}>
                                                <Trans>content:CHANGE_TARIFF</Trans>
                                            </Button>
                                        </>
                                ) :
                                    <Label><Trans>S3 path</Trans>: {cardPrefix ? <b>{cardPrefix}</b> : <i><Trans>cms:NOT_SET</Trans></i>}</Label>
                                }
                                {/* <TextInput data-test-id="contentCardPrefixInput" value={cardPrefix} onChange={(e) => setCardPrefix(e.target.value)} placeholder={t('S3 path')} /> */}
                            </FormEntryFull>}

                        {!isAdmin && availStorageInTariff && data?.results.card_prefix && !s3Failed && <>
                            <div className="form__item">
                                <a href={`/sdn/my/${encodeURIComponent(data.results.card_prefix.slice(data.results.card_prefix.indexOf('/') + 1))}`} target="_blank">
                                    <Button>
                                        <Trans>Show on Disk</Trans>
                                    </Button>
                                </a>
                            </div>
                        </>}
                    </Section>
                    {/*!isAdmin && data?.results.system_prefixes.mediafiles && !s3Failed && <Section label={t('Card files')}>
                        <UnitContentUploader
                            title={t('+ Upload files')}
                            unitId={unitId}
                            media={media}
                            createDraft={createDraft}
                            // onClick={() => setShowTariffModal(true)}
                            blocked={(quotas?.results.storage.limit || 0) <= 0 || !otp.otp_enabled}
                            blockedMsg={(quotas?.results.storage.limit || 0) <= 0 ? '' : (
                                (!otp.otp_enabled) ?
                                    <>
                                        <strong>
                                            <Trans>Two-factor authentication must be enabled to download</Trans>.
                                            <br />
                                        </strong>
                                        <Trans i18nKey="2FAConfigureLink">
                                            To set up, follow the <Link to="/otp" className="link-accent">link</Link>
                                        </Trans>
                                    </>
                                    : ''
                            )}
                            onChange={onMediaChange}
                            editable={role.label != ROLE.SITE_MODERATOR}
                        />
                    </Section>*/}

                    {role.label !== ROLE.SITE_MODERATOR && !myAcc.hideParticipants &&
                        <Section label={t('Offer consumers')} hint={`· ${t('optional')}`}>
                            <MultiSelect
                                dataTestId="contentOfferConsumersMultiSelect"
                                value={offerList}
                                options={availUsers}
                                placeholder={t('Choose a customers')}
                                setValue={setOfferList}
                                top={true}
                            />
                        </Section>
                    }

                    {isAdmin && data?.results.visible_to_guests &&
                        <Section label={t('cms:CMS')}>
                            <h3>
                                <Trans>Show to unregistered users</Trans>
                                {' '}
                                <InputSwitch value={admVisibleToGuests} onChange={(v) => setAdmVisibleToGuests(v)} label={undefined} />
                            </h3>
                            <h3>
                                <Trans>Show on index page</Trans>
                                {' '}
                                <InputSwitch value={admShowOnWelcome} onChange={(v) => setAdmShowOnWelcome(v)} label={undefined} />
                            </h3>
                            <h3>
                                <Trans>Show on recommended</Trans>
                                {' '}
                                <InputSwitch value={admPromoOnWelcome} onChange={(v) => setAdmPromoOnWelcome(v)} label={undefined} />
                            </h3>
                            <Button
                                size="small"
                                disabled={
                                    data.results.adm_visible_to_guests === admVisibleToGuests
                                    && data.results.adm_show_on_welcome === admShowOnWelcome
                                    && data.results.adm_promo_on_welcome === admPromoOnWelcome
                                }
                                onClick={() => {
                                    updateCmsRequest({
                                        id: unitId,
                                        adm_priority: 1,
                                        adm_promo_on_welcome: admPromoOnWelcome,
                                        adm_show_on_welcome: admShowOnWelcome,
                                        adm_visible_to_guests: admVisibleToGuests,
                                    }).unwrap().then(() => {
                                        ignoreUpdate.current = true; if (unitId) fetchUnit(unitId);
                                    });
                                }}><Trans>Save</Trans></Button>
                        </Section>
                    }

                    {isModerator(role.label) &&
                        <FixedButtonGroup
                            checked={allChecked}
                            onSave={onCreate}
                            sendProviderReport={sendProviderReport}
                            setSendProviderReport={setSendProviderReport}
                            hasChanges={hasChanges}
                        />
                    }

                    {!isModerator(role.label) &&
                        // (isEdit && !hasChanges && !createForm) ?
                        // <SendButton onClick={(e) => { e.preventDefault(); setShowSendModal(true); }} />
                        // :
                        <CreateButton onClick={onCreate} isEdit={isEdit} hasChanges={hasChanges} />
                    }
                </ContentAddBody>
            </Form>
        </Content >
    );
}

export default ContentEditorPage;